// src/views/admin/ADConfigBulletins.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";

function Modal({ title, onClose, children, maxWidth = 560 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const CYCLE_LABELS = {
  PRESCOLAIRE: "Préscolaire",
  PRIMAIRE: "Primaire",
  COLLEGE: "Collège",
  LYCEE: "Lycée",
};

const EMPTY = {
  cycle: "",
  annee_scolaire: "",
  slogan_ecole: "",
  devise_ecole: "",
  adresse_complete: "",
  site_web: "",
  email: "",
  texte_logo_pays: "BURKINA FASO",
  sous_texte_logo_pays: "Unité – Progrès – Justice",
  note_maximale: "20.00",
  est_actif: true,
};

// Convertit { "9": "Excellent", "8": "Bien" } <-> [{ seuil: "9", texte: "Excellent" }, ...]
const dictToRows = (dict) =>
  dict && typeof dict === "object"
    ? Object.entries(dict).map(([seuil, texte]) => ({ seuil, texte }))
    : [];
const rowsToDict = (rows) =>
  rows.reduce((acc, r) => {
    if (r.seuil !== "" && r.texte.trim() !== "") acc[r.seuil] = r.texte.trim();
    return acc;
  }, {});

function SeuilsEditor({ label, rows, setRows }) {
  const addRow = () => setRows(prev => [...prev, { seuil: "", texte: "" }]);
  const removeRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) =>
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  return (
    <div className="fg">
      <label>{label}</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 6 }}>
            <input
              type="number" step="0.5" placeholder="Seuil (≥)"
              value={r.seuil} onChange={e => updateRow(i, "seuil", e.target.value)}
              style={{ width: 100 }}
            />
            <input
              type="text" placeholder="Appréciation"
              value={r.texte} onChange={e => updateRow(i, "texte", e.target.value)}
              style={{ flex: 1 }}
            />
            <button type="button" className="ibtn" onClick={() => removeRow(i)}>
              <i className="ti ti-trash" />
            </button>
          </div>
        ))}
        <button type="button" className="bsec" onClick={addRow} style={{ alignSelf: "flex-start" }}>
          <i className="ti ti-plus" />Ajouter un seuil
        </button>
      </div>
    </div>
  );
}

export default function ADConfigBulletins({ slug }) {
  const [annees, setAnnees] = useState([]);
  const [anneeFilt, setAnneeFilt] = useState("");
  const [cycleFilt, setCycleFilt] = useState("");
  const [cyclesEtablissement, setCyclesEtablissement] = useState([]);

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [modal, setModal] = useState(null); // create | edit | delete
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [rowsEducateur, setRowsEducateur] = useState([]);
  const [rowsEcole, setRowsEcole] = useState([]);

  const toast = useToastHook();
  const {
    getAnneesScolaires, getEtablissementCourant,
    getBulletinConfigurations, createBulletinConfiguration,
    updateBulletinConfiguration, updateBulletinConfigurationPartial,
    deleteBulletinConfiguration,
  } = useAdminService();

  useEffect(() => { if (slug) init(); }, [slug]);
  useEffect(() => { if (slug) loadConfigs(); }, [slug, cycleFilt]);

  const init = async () => {
    setLoading(true);
    try {
      const [anneesData, etabData] = await Promise.all([
        getAnneesScolaires(slug, { show_archived: "false" }),
        getEtablissementCourant(slug),
      ]);
      setAnnees(anneesData);
      setCyclesEtablissement(etabData.cycles || []);
      const anneeActive = anneesData.find(a => a.actif);
      setAnneeFilt(String((anneeActive || anneesData[0])?.id || ""));
      await loadConfigs();
    } catch (error) {
      toast.error("Impossible de charger les données de référence");
    } finally {
      setLoading(false);
    }
  };

  const loadConfigs = async () => {
    try {
      const params = cycleFilt ? { cycle: cycleFilt } : {};
      const data = await getBulletinConfigurations(slug, params);
      setConfigs(data);
    } catch (error) {
      toast.error("Impossible de charger les configurations de bulletin");
    }
  };

  const configsFiltrees = configs.filter(
    c => !anneeFilt || String(c.annee_scolaire) === String(anneeFilt)
  );

  const cycleLabel = (val) => CYCLE_LABELS[val] || val;

  // ── CRUD ──────────────────────────────────────────────────────────
  const openCreate = () => {
    setForm({
      ...EMPTY,
      annee_scolaire: anneeFilt,
      cycle: cyclesEtablissement.length === 1 ? cyclesEtablissement[0] : "",
    });
    setLogoFile(null);
    setLogoPreview(null);
    setRowsEducateur([]);
    setRowsEcole([]);
    setModal("create");
  };

  const openEdit = (c) => {
    setSelected(c);
    setForm({
      cycle: c.cycle || "",
      annee_scolaire: c.annee_scolaire || "",
      slogan_ecole: c.slogan_ecole || "",
      devise_ecole: c.devise_ecole || "",
      adresse_complete: c.adresse_complete || "",
      site_web: c.site_web || "",
      email: c.email || "",
      texte_logo_pays: c.texte_logo_pays || "",
      sous_texte_logo_pays: c.sous_texte_logo_pays || "",
      note_maximale: c.note_maximale ?? "20.00",
      est_actif: c.est_actif,
    });
    setLogoFile(null);
    setLogoPreview(c.logo_pays || null);
    setRowsEducateur(dictToRows(c.appreciation_educateur));
    setRowsEcole(dictToRows(c.appreciation_ecole));
    setModal("edit");
  };

  const openDelete = (c) => { setSelected(c); setModal("delete"); };

  const handleFormChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.cycle) { toast.error("Le cycle est requis"); return; }
    if (!form.annee_scolaire) { toast.error("L'année scolaire est requise"); return; }

    setSaving(true);
    try {
      const dataToSend = {
        ...form,
        note_maximale: form.note_maximale || "20.00",
        appreciation_educateur: rowsToDict(rowsEducateur),
        appreciation_ecole: rowsToDict(rowsEcole),
      };

      if (modal === "create") {
        const created = await createBulletinConfiguration(slug, dataToSend, logoFile);
        setConfigs(prev => [created, ...prev]);
        toast.success(`Configuration "${cycleLabel(created.cycle)}" créée avec succès`);
      } else {
        const updated = await updateBulletinConfiguration(slug, selected.id, dataToSend, logoFile);
        setConfigs(prev => prev.map(c => c.id === selected.id ? updated : c));
        toast.success("Configuration modifiée avec succès");
      }
      setModal(null);
    } catch (error) {
      const data = error.response?.data;
      toast.error(typeof data === "object" ? Object.values(data).flat().join("\n") : "Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteBulletinConfiguration(slug, selected.id);
      setConfigs(prev => prev.filter(c => c.id !== selected.id));
      toast.success("Configuration supprimée avec succès");
      setModal(null);
    } catch (error) {
      toast.error("Impossible de supprimer cette configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActif = async (c) => {
    if (c.est_actif) return; // pas de désactivation directe, une seule config active par cycle/année
    try {
      const updated = await updateBulletinConfigurationPartial(slug, c.id, { est_actif: true });
      // Le backend désactive les autres configs du même cycle/année : on recharge pour rester cohérent
      await loadConfigs();
      toast.success(`Configuration "${cycleLabel(updated.cycle)}" activée`);
    } catch (error) {
      toast.error("Impossible d'activer cette configuration");
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2">
        <select value={anneeFilt} onChange={e => setAnneeFilt(e.target.value)}>
          {annees.map(a => <option key={a.id} value={a.id}>{a.nom}{a.actif ? " (active)" : ""}</option>)}
        </select>
        <select value={cycleFilt} onChange={e => setCycleFilt(e.target.value)}>
          <option value="">Tous les cycles</option>
          {cyclesEtablissement.map(c => <option key={c} value={c}>{CYCLE_LABELS[c] || c}</option>)}
        </select>
        <button className="bsec" onClick={loadConfigs}><i className="ti ti-refresh" />Actualiser</button>
        <button className="bprim" onClick={openCreate} disabled={cyclesEtablissement.length === 0}>
          <i className="ti ti-plus" />Nouvelle configuration
        </button>
      </div>

      {cyclesEtablissement.length === 0 && (
        <div style={{ fontSize: 13, color: "var(--h)", marginTop: 10 }}>
          Aucun cycle configuré pour cet établissement — contacte le support pour l'ajouter.
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: "hidden", marginTop: 10 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Cycle</th>
              <th>Année scolaire</th>
              <th>Note max</th>
              <th>Slogan</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {configsFiltrees.map(c => (
              <tr key={c.id}>
                <td style={{ fontWeight: 500 }}>{cycleLabel(c.cycle)}</td>
                <td>{c.annee_scolaire_nom}</td>
                <td>{c.note_maximale}</td>
                <td style={{ fontSize: 13, color: "var(--m)" }}>{c.slogan_ecole || "—"}</td>
                <td>
                  {c.est_actif ? (
                    <span className="pill ok">Active</span>
                  ) : (
                    <button className="pill neu" style={{ border: "none", cursor: "pointer" }} onClick={() => handleToggleActif(c)}>
                      Inactive — activer
                    </button>
                  )}
                </td>
                <td>
                  <div className="act">
                    <button title="Modifier" onClick={() => openEdit(c)}><i className="ti ti-edit" /></button>
                    <button className="del" title="Supprimer" onClick={() => openDelete(c)}><i className="ti ti-trash" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {configsFiltrees.length === 0 && (
              <tr><td colSpan="6" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>
                Aucune configuration trouvée
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Créer/Modifier */}
      {(modal === "create" || modal === "edit") && (
        <Modal title={modal === "create" ? "Nouvelle configuration de bulletin" : "Modifier la configuration"} onClose={() => setModal(null)} maxWidth={640}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9, maxHeight: "70vh", overflowY: "auto", paddingRight: 4 }}>
            <div className="fgrid">
              <div className="fg">
                <label>Cycle *</label>
                <select
                  value={form.cycle}
                  onChange={e => handleFormChange('cycle', e.target.value)}
                  disabled={cyclesEtablissement.length === 1}
                >
                  <option value="">-- Sélectionner --</option>
                  {cyclesEtablissement.map(c => <option key={c} value={c}>{CYCLE_LABELS[c] || c}</option>)}
                </select>
              </div>
              <div className="fg">
                <label>Année scolaire *</label>
                <select value={form.annee_scolaire} onChange={e => handleFormChange('annee_scolaire', e.target.value)}>
                  <option value="">-- Sélectionner --</option>
                  {annees.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
                </select>
              </div>
            </div>

            <div className="fg"><label>Slogan de l'école</label>
              <input value={form.slogan_ecole} onChange={e => handleFormChange('slogan_ecole', e.target.value)} />
            </div>
            <div className="fg"><label>Devise de l'école</label>
              <input value={form.devise_ecole} onChange={e => handleFormChange('devise_ecole', e.target.value)} />
            </div>
            <div className="fg"><label>Adresse complète</label>
              <textarea value={form.adresse_complete} onChange={e => handleFormChange('adresse_complete', e.target.value)} rows={2} />
            </div>
            <div className="fgrid">
              <div className="fg"><label>Site web</label>
                <input value={form.site_web} onChange={e => handleFormChange('site_web', e.target.value)} placeholder="https://…" />
              </div>
              <div className="fg"><label>Email</label>
                <input type="email" value={form.email} onChange={e => handleFormChange('email', e.target.value)} />
              </div>
            </div>

            <div className="fgrid">
              <div className="fg"><label>Texte logo pays</label>
                <input value={form.texte_logo_pays} onChange={e => handleFormChange('texte_logo_pays', e.target.value)} />
              </div>
              <div className="fg"><label>Sous-texte logo pays</label>
                <input value={form.sous_texte_logo_pays} onChange={e => handleFormChange('sous_texte_logo_pays', e.target.value)} />
              </div>
            </div>
            <div className="fg">
              <label>Logo pays</label>
              <input type="file" accept="image/*" onChange={handleLogoChange} />
              {logoPreview && (
                <img src={logoPreview} alt="Logo pays" style={{ height: 48, marginTop: 6, borderRadius: 4 }} />
              )}
            </div>

            <div className="fg"><label>Note maximale</label>
              <input type="number" step="0.01" value={form.note_maximale} onChange={e => handleFormChange('note_maximale', e.target.value)} style={{ width: 120 }} />
            </div>

            <div style={{ borderTop: "0.5px solid var(--b)", paddingTop: 10 }}>
              <SeuilsEditor label="Appréciation éducateur (seuil ≥ note → texte)" rows={rowsEducateur} setRows={setRowsEducateur} />
            </div>
            <div style={{ borderTop: "0.5px solid var(--b)", paddingTop: 10 }}>
              <SeuilsEditor label="Appréciation école (seuil ≥ note → texte)" rows={rowsEcole} setRows={setRowsEcole} />
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
              <input type="checkbox" checked={form.est_actif} onChange={e => handleFormChange('est_actif', e.target.checked)} />
              Configuration active
              <span style={{ color: "var(--m)", fontSize: 12 }}>(désactive automatiquement les autres configs du même cycle/année)</span>
            </label>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modal === "create" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Supprimer */}
      {modal === "delete" && selected && (
        <Modal title="Confirmer la suppression" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>
            Supprimer la configuration <strong>{cycleLabel(selected.cycle)} — {selected.annee_scolaire_nom}</strong> ?
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>
              {saving ? "..." : <><i className="ti ti-trash" />Supprimer</>}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}